import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppContext from 'contexts/AppContext';
import EditCashBoxContext from 'contexts/EditCashBoxContext';
import { getCashBoxEventsByCashBoxId, getCashBoxDetails } from 'services/cloverServices';
import 'styles/pages/CashBoxTransactionsListPage.scss';
import CashBoxEventsTypes from 'types/enums/CashBoxEventsTypes';
import CashBoxEvent from 'types/models/CashBoxEvent';
import CashBoxTransaction from 'types/models/CashBoxTransaction';
import CashBoxTransactionTypes from 'types/enums/CashBoxTransactionTypes';
import { valueToCurrencyFormat } from 'utils/parseCurrency';
import Order from 'types/models/Order';
import { CashBoxCloseResponse } from 'types/models/CashBoxCloseResponse';
import CashBoxAmounts from 'components/CashBoxAmounts';

export default (): JSX.Element => {
  const Headers = ['Fecha', 'Id', 'Tipo', 'Pago', 'Valor'];

  const history = useHistory();
  const { selectedCashBox, setSelectedCashBox } = React.useContext(EditCashBoxContext);
  const { setIsLoading } = React.useContext(AppContext);
  const { t } = useTranslation();
  const [cashBoxCloseResponse, setCashBoxCloseResponse] = React.useState<CashBoxCloseResponse | null>(null);

  const load = React.useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const cashBoxEventsResponse = await getCashBoxEventsByCashBoxId(id);
      if (selectedCashBox && cashBoxEventsResponse) {
        setSelectedCashBox({ ...selectedCashBox, cashBoxEvents: cashBoxEventsResponse });
      }
      const getCashBoxCloseResponse = await getCashBoxDetails(id);
      if (getCashBoxCloseResponse) {
        setCashBoxCloseResponse(getCashBoxCloseResponse);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!selectedCashBox) {
      history.push('/private/cash-boxeses');
    } else {
      if (!selectedCashBox.cashBoxEvents) {
        load(selectedCashBox.id);
      }
    }
  }, [selectedCashBox]);

  const getPaymentTypeInfo = (event: CashBoxEvent): { type: string; payment: string } => {
    switch (event.type) {
      case CashBoxEventsTypes.Transaction: {
        const transaction = event.data as CashBoxTransaction;
        if (transaction.type === CashBoxTransactionTypes.Deposit) {
          return { type: t('cash-boxes-transaction'), payment: t('cash-boxes-deposit') };
        }
        return { type: t('cash-boxes-transaction'), payment: t('cash-boxes-withdrawal') };
      }
      case CashBoxEventsTypes.Order: {
        const order = event.data as Order;
        let payment = t('cash-boxes-card');
        if (order.refundOfOrder) {
          return { type: t('refund'), payment: t('cash-boxes-cash') };
        }
        if (order.fiservQrDataPayment) payment = 'QR';
        if (order.isCash) payment = t('cash-boxes-cash');

        return { type: t('cash-boxes-order'), payment };
      }
      case CashBoxEventsTypes.Open:
        return { type: t('cash-boxes-open'), payment: t('cash-boxes-open') };
      case CashBoxEventsTypes.Close:
        return { type: t('cash-boxes-total-close'), payment: t('cash-boxes-total-close') };
      case CashBoxEventsTypes.PartialClose:
        return { type: t('cash-boxes-partial-close'), payment: t('cash-boxes-partial-close') };
      default:
        return { type: '', payment: '' };
    }
  };

  const getPaymentAmount = (event: CashBoxEvent): number => {
    switch (event.type) {
      case CashBoxEventsTypes.Transaction:
        const transaction = event.data as CashBoxTransaction;
        return transaction.type === CashBoxTransactionTypes.Deposit ? transaction.total : transaction.total * -1;
      case CashBoxEventsTypes.Order:
        const order = event.data as Order;
        return order.total;
      default:
        return 0;
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const getColorAmount = (amount: number): string => {
    if (amount > 0) {
      return 'positive-amount';
    } else if (amount < 0) {
      return 'negative-amount';
    } else {
      return 'zero-amount';
    }
  };

  const renderCashBoxList = (cashBoxEvents: CashBoxEvent[]): JSX.Element[] => {
    return cashBoxEvents.map((event, index) => {
      const { type, payment } = getPaymentTypeInfo(event);
      const amount = getPaymentAmount(event);
      const getOrderId = (event: CashBoxEvent): string | number => {
        if (event.type === CashBoxEventsTypes.Order) {
          const order = event.data as Order;
          return order.refundOfOrder ? `dev #${order.refundOfOrder}` : order.id;
        }
        return '-';
      };

      return (
        <div key={index} className={'row-container'}>
          <div className={'text-container'}>{event.createdAt ? formatDate(event.createdAt) : 'No hay fecha'}</div>
          <div className="chip-container">
            {event.type === CashBoxEventsTypes.Order
              ? getOrderId(event)
              : event.type === CashBoxEventsTypes.Transaction
              ? event.data?.id
              : '-'}
          </div>
          <div className="chip-container">{type}</div>
          <div className={'chip-container'}>{payment}</div>
          <div className={`amount-text ${getColorAmount(amount)}`}>
            {amount > 0 && '+'}
            {valueToCurrencyFormat(amount ?? 0)}
          </div>
        </div>
      );
    });
  };

  if (!selectedCashBox || !selectedCashBox.cashBoxEvents) return <></>;
  return (
    <div className={'main-container'}>
      <h2>
        {t('cash-box')} #{selectedCashBox.id}
      </h2>

      <div className={'cash-box-balance-container'}>
        <h3>{t('cash-boxes-total-balances')}</h3>

        <div className={'cash-box-balances'}>
          <div className={'widget-container'}>
            <h4>{t('cash-boxes-cash-balance')}</h4>
            <p>{valueToCurrencyFormat(selectedCashBox.cashBalance ?? 0)}</p>
          </div>
          <div className={'widget-container'}>
            <h4>{t('cash-boxes-card-balance')}</h4>
            <p>{valueToCurrencyFormat(selectedCashBox.cardBalance ?? 0)}</p>
          </div>
          <div className={'widget-container'}>
            <h4>{t('cash-boxes-qr-balance')}</h4>
            <p>{valueToCurrencyFormat(selectedCashBox.qrBalance ?? 0)}</p>
          </div>

          <div className={'widget-container'}>
            <h4>{t('cash-boxes-total')}</h4>
            <p>
              {valueToCurrencyFormat(
                (+selectedCashBox.qrBalance || 0) +
                  (+selectedCashBox.cardBalance || 0) +
                  (+selectedCashBox.cashBalance || 0),
              )}
            </p>
          </div>
        </div>
      </div>
      <div className={'header-container'}>
        {Headers.map((header, index) => {
          return (
            <div className={'header-text'} key={index}>
              {header}
            </div>
          );
        })}
      </div>

      <div className="list-container">{renderCashBoxList(selectedCashBox.cashBoxEvents as CashBoxEvent[])}</div>
      <CashBoxAmounts cashBoxCloseResponse={cashBoxCloseResponse} />
    </div>
  );
};
